import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getMe } from '../api/saasApi';
import { MEMBERSHIP_CHANGED_EVENT } from '../api/axios';
import { useAuth } from './AuthContext';

const MeContext = createContext({
  me: null,
  loading: false,
  loaded: false,
  can: () => false,
  isPlatformAdmin: false,
  isOwner: false,
  isReadOnly: false,
  isExempt: false,
  billing: null,
  membershipStatus: null,
  isActiveMember: false,
  refresh: () => {},
});

export const MeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(false);
  // Distinct from `loading`: lets consumers tell "not fetched yet" from
  // "fetched and there is genuinely no membership", so the waiting screen
  // never flashes while /api/Me is still in flight.
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setMe(null);
      setLoaded(false);
      return;
    }
    setLoading(true);
    try {
      const res = await getMe();
      setMe(res.data?.data || null);
    } catch (e) {
      setMe(null);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Re-check when the API reports our membership changed mid-session.
  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener(MEMBERSHIP_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(MEMBERSHIP_CHANGED_EVENT, onChange);
  }, [refresh]);

  const permissions = useMemo(() => me?.permissions || [], [me]);
  const isPlatformAdmin = !!me?.isPlatformAdmin;
  const isOwner = !!me?.isOwner;
  const membershipStatus = me?.membershipStatus || null;

  // Access to clinical data requires an approved membership in a real clinic.
  // Platform admins operate above tenants, so they are never gated by this.
  //
  // A backend that predates the membership feature returns no membershipStatus
  // at all. Treat that as approved rather than locking every existing user out
  // of a deployment where the frontend has shipped ahead of the API — this gate
  // is presentational, and MembershipEnforcementMiddleware still has the final
  // say on every request.
  //
  // A deactivated account is locked out whoever it is — platform admins included.
  const isDeactivated = membershipStatus === 'Deactivated';
  const isActiveMember = !isDeactivated && (
    isPlatformAdmin || (!!me?.tenant && (membershipStatus === null || membershipStatus === 'Active')));

  // Platform admins implicitly pass any tenant permission check.
  const can = useCallback(
    (permission) => isPlatformAdmin || permissions.includes(permission),
    [permissions, isPlatformAdmin]
  );

  const billing = me?.billing || null;
  const isReadOnly = !!billing?.isSuspended;
  // A clinic is payment-gated unless it is the exempt legacy tenant.
  const isExempt = !!billing?.isExempt;

  return (
    <MeContext.Provider
      value={{
        me,
        loading,
        loaded,
        can,
        isPlatformAdmin,
        isOwner,
        isReadOnly,
        isExempt,
        billing,
        membershipStatus,
        isActiveMember,
        refresh,
      }}
    >
      {children}
    </MeContext.Provider>
  );
};

export const useMe = () => useContext(MeContext);
